import { store } from '@/Infrastructure/State';
import httpClient from '@/modules/cart/services/api/client';
import environment from '@/modules/perseo-billing/environments/environment';
import type { IOrderIntentResponse } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import type { IOrder } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';
import type {
  IConfirmOrderIntentBody,
  IPaymentRequest
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { getDocumentType as getDocumentTypeFromUser } from '@/modules/perseo-billing/store/user/user.reducer';
import { getSeonSessionID } from '@/modules/perseo-billing/utils/get-seon-session';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';
import {
  getWeddingID,
  isWeddingFlow
} from '@/modules/shared/utils/isWeddingFlow';

const CREATE_ORDER_CONFIG = {
  timeout: 100000
};

const {
  CREATE_NEW_ORDER,
  CREATE_NEW_WEDDING_ORDER,
  CREATE_NEW_ORDER_INTENT,
  CREATE_NEW_WEDDING_ORDER_INTENT,
  CONFIRM_ORDER_INTENT,
  CONFIRM_WEDDING_ORDER_INTENT,
  CMEDIA_PURCHASE_URL
} = environment;

const getDocumentType = (): string => {
  const state = store.getState();

  // Priority 1: For registered users, get documentType from user store
  // (populated from customer endpoint via CustomerContext)
  const userDocumentType = getDocumentTypeFromUser();
  if (userDocumentType) {
    return userDocumentType;
  }

  // Priority 2: For guest users, get documentType from shippingAddress
  const shippingAddress = state?.cart?.data?.shippingAddress;
  if (shippingAddress?.documentType) {
    return shippingAddress.documentType;
  }

  // Priority 3: Fallback to RUT for backward compatibility
  // (legacy users without documentType field)
  return IdentificationDocumentTypes.RUT;
};

export async function postCreatePurchaseOrder(
  payload: IPaymentRequest
): Promise<IOrder> {
  const isWedding = isWeddingFlow();
  const weddingId = getWeddingID();
  const seonFingerprint = await getSeonSessionID();

  const urlForWedding = `${CREATE_NEW_WEDDING_ORDER}/${weddingId}`;

  const url = isWedding ? urlForWedding : CREATE_NEW_ORDER;
  const response = await httpClient.post(
    url,
    { ...payload, ...(seonFingerprint && { seonFingerprint }) },
    CREATE_ORDER_CONFIG
  );
  return Promise.resolve(response?.data);
}

export async function postCreateOrderIntent(
  payload: IPaymentRequest
): Promise<IOrderIntentResponse> {
  const isWedding = isWeddingFlow();
  const documentType = getDocumentType();
  const config = {
    ...CREATE_ORDER_CONFIG,
    ...(documentType && {
      headers: { 'x-document-type': documentType }
    })
  };

  if (isWedding) {
    const weddingId = getWeddingID();
    const url = CREATE_NEW_WEDDING_ORDER_INTENT;
    const weddingUrl = url.replace(':idWedding', weddingId);
    const response = await httpClient.post(weddingUrl, payload, config);
    return Promise.resolve(response?.data);
  }

  const url = CREATE_NEW_ORDER_INTENT;
  const response = await httpClient.post(url, payload, config);
  return Promise.resolve(response?.data);
}

export async function postConfirmOrderIntent(
  payload: IConfirmOrderIntentBody
): Promise<IOrder> {
  const isWedding = isWeddingFlow();
  const documentType = getDocumentType();

  const configWithNoRetry = {
    ...CREATE_ORDER_CONFIG,
    metadata: { skipAuthRetry: true },
    ...(documentType && {
      headers: { 'x-document-type': documentType }
    })
  };

  if (isWedding) {
    const weddingId = getWeddingID();
    const url = CONFIRM_WEDDING_ORDER_INTENT;
    const weddingUrl = url.replace(':idWedding', weddingId);
    const response = await httpClient.post(
      weddingUrl,
      payload,
      configWithNoRetry
    );
    return Promise.resolve(response?.data);
  }

  const url = CONFIRM_ORDER_INTENT;
  const response = await httpClient.post(url, payload, configWithNoRetry);
  return Promise.resolve(response?.data);
}
export async function postCmediaPurchase(
  payload: unknown,
  platform: string
): Promise<unknown> {
  const response = await httpClient.post(CMEDIA_PURCHASE_URL, payload, {
    headers: { platform }
  });
  return Promise.resolve(response?.data?.data);
}
